// Product Development View/Edit - Clinical Supply
// Single file handling both view and edit modes
// Shows only Product Details and Status Selection (matching the 3-step stepper)
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  makeStyles,
  CircularProgress,
  Button,
  Divider,
  IconButton,
  Dialog,
} from "@material-ui/core";
import ArrowBack from "@material-ui/icons/ArrowBack";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";
import ProductDetails from "../Stepper/steps/ProductDetails";
import StatusSelection from "../Stepper/steps/StatusSelection";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import isEqual from 'lodash.isequal';
import StyledDialog from "../../../../../../common/models/Dialog";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    width: "100%",
  },
  stepperHeader: {
    padding: "32px 0",
    textAlign: "center",
    backgroundColor: "#F5F9FF",
    position: "relative",
  },
  heading: {
    fontWeight: 500,
    fontSize: 30,
    lineHeight: "100%",
    marginBottom: 20,
  },
  subheading: {
    fontWeight: 400,
    fontSize: 16,
    lineHeight: "100%",
  },
  iconButtonStyle: {
    color: "black",
    border: "1px solid black",
    borderRadius: "5px",
    padding: "4px",
  },
  sectionPaper: {
    padding: 0,
    marginBottom: 0,
  },
  reviewSection: {
    marginBottom: 50,
    position: "relative",
  },
  actionButtons: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 0,
    marginBottom: 0,
  },
  editButton: {
    backgroundColor: "#ec6535",
    color: "white",
    "&:hover": {
      backgroundColor: "#d45328",
    },
  },
  saveButton: {
    borderRadius: "5rem",
    marginLeft: "1rem",
  },
  discardButton: {
    borderRadius: "5rem",
  },
  buttonProgress: {
    color: "white",
    marginLeft: theme.spacing(1),
  },
  updateChangesButton: {
    backgroundColor: "#ec6535",
    color: "#fff",
    borderRadius: 40,
    minWidth: 180,
    boxShadow: "none",
    textTransform: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 32,
    '&:hover': {
      backgroundColor: "#d45328",
      boxShadow: "none",
    },
  },
  noProductDataText: {
    marginTop: 50,
    marginBottom: 50,
    textAlign: 'center',
  },
  formTitle: {
    fontSize: "32px",
    lineHeight: "39px",
    fontWeight: 700,
    margin: "55px 0 70px 0",
    position: "relative",
    "&::after": {
      content: '""',
      display: "block",
      width: "55px",
      height: "5px",
      backgroundColor: "#707070",
      position: "absolute",
      left: 0,
      top: "60px",
    },
  },
}));

// Normalization helpers
function normalizeStatusData(statusData) {
  if (!statusData) return {};
  const normalizeArray = arr =>
    (arr || []).map(status => {
      if (status.value !== undefined && status.activated === undefined) {
        return { ...status, activated: status.value };
      }
      if (status.activated !== undefined && status.value === undefined) {
        return { ...status, value: status.activated };
      }
      if (status.activated !== undefined && status.value !== undefined) {
        return { ...status, value: status.activated, activated: status.activated };
      }
      return { ...status, value: false, activated: false };
    });

  // Only PO and Batch for Clinical Supply Product Development
  return {
    poStatuses: normalizeArray(statusData.poStatuses || []),
    batchStatuses: normalizeArray(statusData.batchStatuses || []),
  };
}

// Helper function to sort materials
const sortMaterials = (materialDetails) => {
  if (!materialDetails?.productMaterialsList) return materialDetails;
  return materialDetails;
};

// Update the isSectionChanged function for fgStatuses
const isSectionChanged = (current, original, sectionKey) => {
  if (sectionKey === 'fgStatuses') {
    const normCurrent = normalizeStatusData(current);
    const normOriginal = normalizeStatusData(original);

    function getStatusMap(arr) {
      const map = {};
      (arr || []).forEach(s => {
        map[s.status_id] = !!s.activated;
      });
      return map;
    }

    function compareStatusMaps(mapA, mapB) {
      // Only compare keys that exist in the current master list (mapA)
      for (const key of Object.keys(mapA)) {
        if ((mapA[key] || false) !== (mapB[key] || false)) {
          return false;
        }
      }
      return true;
    }

    // Only check PO and Batch for Clinical Supply Product Development
    const poStatusCurrent = getStatusMap(normCurrent.poStatuses);
    const poStatusOriginal = getStatusMap(normOriginal.poStatuses);
    const poStatusChanged = !compareStatusMaps(poStatusCurrent, poStatusOriginal);

    const batchStatusCurrent = getStatusMap(normCurrent.batchStatuses);
    const batchStatusOriginal = getStatusMap(normOriginal.batchStatuses);
    const batchStatusChanged = !compareStatusMaps(batchStatusCurrent, batchStatusOriginal);

    return poStatusChanged || batchStatusChanged;
  }
  if (sectionKey === 'productDetails') {
    return !isEqual(current, original);
  }
  return !isEqual(current, original);
};

const ViewProductDevelopment = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { roleName, organizationName, ecosystemId } = useParams();
  const {
    selectedProduct,
    configuredProductDetails,
    selectedProductFormData,
    isEdit,
  } = location.state || {};
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editSection, setEditSection] = useState(null);
  const [formData, setFormData] = useState({});
  const [originalFormData, setOriginalFormData] = useState({});
  const [saving, setSaving] = useState({});
  const [pageLoading, setPageLoading] = useState(false);
  const [isStatusEditMode, setIsStatusEditMode] = useState(false);
  const [isProductDetailsEditMode, setIsProductDetailsEditMode] = useState(false);
  const [currentCollabrixProductID, setCurrentCollabrixProductID] = useState(
    formData?.productDetails?.collabrixProductID || selectedProduct?.collabrixProductID
  );
  const [isProductDetailsValid, setIsProductDetailsValid] = useState(true);
  const [isStatusSelectionValid, setIsStatusSelectionValid] = useState(true);
  const [productDetailsErrors, setProductDetailsErrors] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [productDetailsKey, setProductDetailsKey] = useState(0);
  const productDetailsRef = useRef(null);
  const statusSelectionRef = useRef(null);

  // Determine if we're in edit mode - check both URL pathname and location.state
  // In view mode, render like Commercial Supply ViewProduct (no edit buttons, components render their own titles)
  // In edit mode, render like Commercial Supply ViewProductEdit (with edit icons)
  const isEditMode = location.pathname.includes('/editProduct') || isEdit === true;

  // Change detection - only check productDetails, no materialDetails for Clinical Supply Product Development
  const isProductDetailsChanged = useMemo(() => {
    return isSectionChanged(formData?.productDetails, originalFormData?.productDetails, 'productDetails');
  }, [formData?.productDetails, originalFormData?.productDetails]);

  const isStatusSelectionChanged = useMemo(() => 
    isSectionChanged(formData?.fgStatuses, originalFormData?.fgStatuses, 'fgStatuses'), 
    [formData?.fgStatuses, originalFormData?.fgStatuses]
  );

  const anySectionChanged = useMemo(() => 
    isProductDetailsChanged || isStatusSelectionChanged,
    [isProductDetailsChanged, isStatusSelectionChanged]
  );

  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");

    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, []);

  // Fetch FG Statuses API function (same as Commercial Supply)
  const fetchFGStatusesAPI = useCallback(async () => {
    if (!apiBaseUrl) return null;
    try {
      const response = await axios.get(`${apiBaseUrl}/fetchFGStatuses/true`);
      if (response?.data?.data) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching FG statuses:", error);
      return null;
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    const fetchProductDetails = async () => {
      if (configuredProductDetails || selectedProductFormData) {
        const productDataToUse = configuredProductDetails || selectedProductFormData;
        setFormData({
          productDetails: {
            productCode: productDataToUse?.productDetails?.productCode,
            productDescription: productDataToUse?.productDetails?.productDescription,
            brand: productDataToUse?.productDetails?.brand,
            status: productDataToUse?.productDetails?.status,
            supplyTypeID: productDataToUse?.productDetails?.supplyTypeID,
            productModality: productDataToUse?.productDetails?.drugTypeName,
            drugTypeID: productDataToUse?.productDetails?.drugTypeID,
            supplyTypeName: productDataToUse?.productDetails?.supplyTypeName,
            collabrixProductID: productDataToUse?.productDetails?.collabrixProductID,
            // Include all fields needed for Clinical Supply Product Development view
            productTypeID: productDataToUse?.productDetails?.productTypeID,
            productTypeName: productDataToUse?.productDetails?.productTypeName,
            facilityTypeID: productDataToUse?.productDetails?.facilityTypeID,
            manufacturingFacilityType: productDataToUse?.productDetails?.facilityTypeName,
            vendorName: productDataToUse?.productDetails?.vendorName,
            vendorMosymphonyID: productDataToUse?.productDetails?.vendorMosymphonyID,
            cmoProductCode: productDataToUse?.productDetails?.cmoProductCode,
            cmoProductDescription: productDataToUse?.productDetails?.cmoProductDescription,
            cmoProductDetailsList: productDataToUse?.productDetails?.cmoProductDetailsList || [],
            sponsorUOM: productDataToUse?.productDetails?.sponsorUOM,
            cmoUOM: productDataToUse?.productDetails?.cmoUOM,
            receivingPlantIDFromSponsorERP: productDataToUse?.productDetails?.receivingPlantIDFromSponsorERP,
            receivingStorageLocationIDsFromSponsorERP: productDataToUse?.productDetails?.receivingStorageLocationIDsFromSponsorERP,
            // Map qaCheckRequired from processFlowConfiguration (like commercial supply)
            qaCheckRequired: productDataToUse?.processFlowConfiguration?.sponsorCQAApprovalReq ?? 
                            productDataToUse?.processFlowConfiguration?.qaCheckRequired ?? 
                            productDataToUse?.productDetails?.qaCheckRequired ?? 
                            productDataToUse?.productDetails?.sponsorCQAApprovalReq ?? false,
            cmoPlantId: productDataToUse?.productDetails?.cmoPlantId,
          },
          // Include processFlowConfiguration in formData
          processFlowConfiguration: productDataToUse?.processFlowConfiguration || {},
          // Do NOT include materialDetails - Clinical Supply Product Development doesn't show Material Details
          fgStatuses: normalizeStatusData(productDataToUse?.productFGStatuses?.fgStatusesJSON || productDataToUse?.fgStatuses || {}),
        });
        // Create productDetails object for deep copy - use productDataToUse, not formData (since setFormData is async)
        const productDetailsForCopy = {
          productCode: productDataToUse?.productDetails?.productCode,
          productDescription: productDataToUse?.productDetails?.productDescription,
          brand: productDataToUse?.productDetails?.brand,
          status: productDataToUse?.productDetails?.status,
          supplyTypeID: productDataToUse?.productDetails?.supplyTypeID,
          productModality: productDataToUse?.productDetails?.drugTypeName,
          drugTypeID: productDataToUse?.productDetails?.drugTypeID,
          supplyTypeName: productDataToUse?.productDetails?.supplyTypeName,
          collabrixProductID: productDataToUse?.productDetails?.collabrixProductID,
          productTypeID: productDataToUse?.productDetails?.productTypeID,
          productTypeName: productDataToUse?.productDetails?.productTypeName,
          facilityTypeID: productDataToUse?.productDetails?.facilityTypeID,
          manufacturingFacilityType: productDataToUse?.productDetails?.facilityTypeName,
          vendorName: productDataToUse?.productDetails?.vendorName,
          vendorMosymphonyID: productDataToUse?.productDetails?.vendorMosymphonyID,
          cmoProductCode: productDataToUse?.productDetails?.cmoProductCode,
          cmoProductDescription: productDataToUse?.productDetails?.cmoProductDescription,
          cmoProductDetailsList: productDataToUse?.productDetails?.cmoProductDetailsList || [],
          sponsorUOM: productDataToUse?.productDetails?.sponsorUOM,
          cmoUOM: productDataToUse?.productDetails?.cmoUOM,
          receivingPlantIDFromSponsorERP: productDataToUse?.productDetails?.receivingPlantIDFromSponsorERP,
          receivingStorageLocationIDsFromSponsorERP: productDataToUse?.productDetails?.receivingStorageLocationIDsFromSponsorERP,
          // Map qaCheckRequired from processFlowConfiguration (like commercial supply)
          qaCheckRequired: productDataToUse?.processFlowConfiguration?.sponsorCQAApprovalReq ?? 
                          productDataToUse?.processFlowConfiguration?.qaCheckRequired ?? 
                          productDataToUse?.productDetails?.qaCheckRequired ?? 
                          productDataToUse?.productDetails?.sponsorCQAApprovalReq ?? false,
          cmoPlantId: productDataToUse?.productDetails?.cmoPlantId,
        };
        
        setOriginalFormData({
          productDetails: productDetailsForCopy ? JSON.parse(JSON.stringify(productDetailsForCopy)) : {}, // Deep copy to preserve all fields
          // Do NOT include materialDetails - Clinical Supply Product Development doesn't show Material Details
          fgStatuses: normalizeStatusData(productDataToUse?.productFGStatuses?.fgStatusesJSON || productDataToUse?.fgStatuses || {}),
        });
        setLoading(false);
        return;
      }

      if (selectedProduct?.collabrixProductID) {
        try {
          setLoading(true);
          if (apiBaseUrl) {
            const response = await axios.get(
              `${apiBaseUrl}/fetchProductDetails/${selectedProduct.collabrixProductID}`
            );

            if (response.data && response.data.status) {
              const apiData = response.data.data;
              setFormData({
                productDetails: {
                  ...apiData.productDetails,
                  productModality: apiData.productDetails?.drugTypeName,
                  manufacturingFacilityType: apiData.productDetails?.facilityTypeName,
                  // Map qaCheckRequired from processFlowConfiguration (like commercial supply)
                  qaCheckRequired: apiData.processFlowConfiguration?.sponsorCQAApprovalReq ?? 
                                  apiData.processFlowConfiguration?.qaCheckRequired ?? 
                                  apiData.productDetails?.qaCheckRequired ?? 
                                  apiData.productDetails?.sponsorCQAApprovalReq ?? false,
                },
                // Include processFlowConfiguration in formData
                processFlowConfiguration: apiData.processFlowConfiguration || {},
                // Do NOT include materialDetails - Clinical Supply Product Development doesn't show Material Details
                fgStatuses: normalizeStatusData(apiData.productFGStatuses?.fgStatusesJSON || apiData.fgStatuses || {}),
              });
              setOriginalFormData({
                productDetails: apiData.productDetails ? JSON.parse(JSON.stringify({
                  ...apiData.productDetails,
                  productModality: apiData.productDetails?.drugTypeName,
                  manufacturingFacilityType: apiData.productDetails?.facilityTypeName,
                  // Map qaCheckRequired from processFlowConfiguration (like commercial supply)
                  qaCheckRequired: apiData.processFlowConfiguration?.sponsorCQAApprovalReq ?? 
                                  apiData.processFlowConfiguration?.qaCheckRequired ?? 
                                  apiData.productDetails?.qaCheckRequired ?? 
                                  apiData.productDetails?.sponsorCQAApprovalReq ?? false,
                })) : {}, // Deep copy to preserve all fields including cmoProductDetailsList
                processFlowConfiguration: apiData.processFlowConfiguration ? JSON.parse(JSON.stringify(apiData.processFlowConfiguration)) : {},
                // Do NOT include materialDetails - Clinical Supply Product Development doesn't show Material Details
                fgStatuses: normalizeStatusData(apiData.productFGStatuses?.fgStatusesJSON || apiData.fgStatuses || {}),
              });
              if (apiData.productDetails?.collabrixProductID) {
                setCurrentCollabrixProductID(apiData.productDetails.collabrixProductID);
              }
            } else {
              setError("Failed to fetch product details");
            }
          } else {
            setError("Missing API configuration");
          }
        } catch (err) {
          console.error("Error fetching product details:", err);
          setError("An error occurred while fetching product details");
        } finally {
          setLoading(false);
        }
      } else {
        setError("No product selected");
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [selectedProduct, configuredProductDetails, selectedProductFormData, apiBaseUrl]);

  const handleGoBack = () => {
    history.goBack();
  };

  const handleEdit = () => {
    if (selectedProduct) {
      history.push({
        pathname: "productDevelopment/editProduct",
        state: {
          selectedProduct,
          configuredProductDetails: formData,
          selectedProductFormData: formData,
          isEdit: true,
        },
      });
    }
  };

  const updateStepData = (step, data) => {
    setFormData((prev) => ({
      ...prev,
      [step]: data,
    }));
  };

  const saveHandlers = {
    productDetails: async () => {
      if (!isProductDetailsChanged) return;
      
      // Don't update originalFormData here - that should only happen after the global "Update Changes" is successful
      // The individual section save is just for UI purposes to exit edit mode and validate changes
      // Matching Commercial Supply behavior
      
      setEditSection(null);
      setIsProductDetailsEditMode(false);
    },
    statusSelection: async () => {
      if (!isStatusSelectionChanged) {
        setEditSection(null);
        setIsStatusEditMode(false);
        return;
      }
      
      // Don't update originalFormData here - that should only happen after the global "Update Changes" is successful
      // Matching Commercial Supply behavior
      
      setEditSection(null);
      setIsStatusEditMode(false);
    },
  };

  const handleDiscard = (section) => {
    if (section === 'productDetails') {
      setFormData((prev) => ({
        ...prev,
        productDetails: JSON.parse(JSON.stringify(originalFormData?.productDetails)),
        // No materialDetails for Clinical Supply Product Development
      }));
      setIsProductDetailsEditMode(false);
      setProductDetailsErrors([]); // Clear validation errors
      // Force ProductDetails component to remount with fresh data
      setProductDetailsKey(prev => prev + 1);
    } else if (section === 'statusSelection') {
      setFormData((prev) => ({
        ...prev,
        fgStatuses: JSON.parse(JSON.stringify(originalFormData?.fgStatuses))
      }));
      setIsStatusEditMode(false);
    }
    setEditSection(null);
  };

  // Only 2 sections for Clinical Supply Product Development: Product Details and Status Selection
  const sections = [
    {
      key: "productDetails",
      label: "Product Details",
      Component: ProductDetails,
      stepKey: "productDetails",
      additionalProps: {
        editActiveProduct: isEditMode,
        onValidationChange: setIsProductDetailsValid,
        onValidationError: setProductDetailsErrors,
        isProductDetailsEditMode: isProductDetailsEditMode,
        // Do NOT pass materialDetails - Clinical Supply Product Development doesn't show Material Details tables
        skipReplicateDialog: false,
        setSkipReplicateDialog: () => {},
      },
    },
    {
      key: "statusSelection",
      label: "Status Selection",
      Component: StatusSelection,
      stepKey: "fgStatuses",
      additionalProps: {
        readOnly: !isEditMode || editSection === null || editSection !== "statusSelection",
        isStatusEditMode: isStatusEditMode,
        onValidationChange: setIsStatusSelectionValid,
        isStepSaved: false,
        onStepSaved: () => {},
        fetchFGStatusesAPI: fetchFGStatusesAPI,
        hideStepActions: true, // Hide navigation buttons in view/edit mode
      }
    },
  ];

  const getValidationErrorMessage = (section) => {
    if (saving[section]) return "Saving changes...";
    const errors = [];
    if (section === 'productDetails') {
      if (!isProductDetailsValid && (!productDetailsErrors || productDetailsErrors.length === 0)) {
        errors.push("Please fill in all required fields");
      }
    } else if (section === 'statusSelection') {
      if (!isStatusSelectionValid) errors.push("Please select at least one status");
    }
    return errors.length > 0 ? errors[0] : null;
  };

  const handleUpdateAllChanges = async () => {
    if (!anySectionChanged || !apiBaseUrl) return;
    
    setPageLoading(true);
    try {
      const normalizedFgStatuses = normalizeStatusData(formData?.fgStatuses || {});
      
      // Construct cmoProductDetailsList from direct fields if not already present
      const productDetails = formData?.productDetails || {};
      let cmoProductDetailsList = productDetails.cmoProductDetailsList || [];
      
      // If cmoProductDetailsList is empty or doesn't have valid data, construct it from direct fields
      if (!cmoProductDetailsList || cmoProductDetailsList.length === 0 || 
          !cmoProductDetailsList[0]?.cmoProductCode) {
        // Check if we have any CMO field values to create the list
        if (productDetails.cmoProductCode || productDetails.cmoProductDescription || productDetails.cmoUOM) {
          cmoProductDetailsList = [{
            cmoProductCode: productDetails.cmoProductCode || "",
            cmoProductDescription: productDetails.cmoProductDescription || "",
            cmoUOM: productDetails.cmoUOM || "",
          }];
        } else {
          cmoProductDetailsList = [];
        }
      }

      // Remove direct CMO fields and qaCheckRequired from productDetails
      // qaCheckRequired goes to processFlowConfiguration (like commercial supply)
      const { cmoProductCode, cmoProductDescription, cmoUOM, qaCheckRequired, ...productDetailsWithoutCMO } = productDetails;
      
      let payload = {
        productDetails: {
          ...productDetailsWithoutCMO,
          // Use cmoProductDetailsList array format instead of direct fields
          cmoProductDetailsList: cmoProductDetailsList,
        },
        // Map qaCheckRequired to processFlowConfiguration (like commercial supply)
        ...(isProductDetailsChanged && {
          updateProcessFlowConfigFlag: true,
          processFlowConfiguration: {
            sponsorCQAApprovalReq: productDetails.qaCheckRequired === true ? true : false, // true if selected YES, false if NO
          },
        }),
        // Do NOT include materialDetails - Clinical Supply Product Development doesn't have Material Details
        ...(isProductDetailsChanged && { updateProductDetailsFlag: true }),
        ...(isStatusSelectionChanged && {
          updateFGStatusesFlag: true,
          productFGStatuses: {
            fgStatusesJSON: {
              // Only PO and Batch for Clinical Supply Product Development
              poStatuses: (normalizedFgStatuses?.poStatuses || [])
                .filter((s) => s.activated)
                .map(({ status_id, status_name, activated }) => ({ status_id, status_name, activated })),
              batchStatuses: (normalizedFgStatuses?.batchStatuses || [])
                .filter((s) => s.activated)
                .map(({ status_id, status_name, activated }) => ({ status_id, status_name, activated })),
            },
          },
        }),
      };

      const response = await axios.post(`${apiBaseUrl}/updateProductDetails`, payload);
      if (response.data && response.data.collabrixProductID) {
        setCurrentCollabrixProductID(response.data.collabrixProductID);
        const fetchResponse = await axios.get(`${apiBaseUrl}/fetchProductDetails/${response.data.collabrixProductID}`);
        if (fetchResponse.data && fetchResponse.data.status) {
          const apiData = fetchResponse.data.data;
          setFormData({
            productDetails: {
              ...apiData.productDetails,
              productModality: apiData.productDetails?.drugTypeName,
              manufacturingFacilityType: apiData.productDetails?.facilityTypeName,
              // Map qaCheckRequired from processFlowConfiguration (like commercial supply)
              qaCheckRequired: apiData.processFlowConfiguration?.sponsorCQAApprovalReq ?? 
                              apiData.processFlowConfiguration?.qaCheckRequired ?? 
                              apiData.productDetails?.qaCheckRequired ?? 
                              apiData.productDetails?.sponsorCQAApprovalReq ?? false,
            },
            // Include processFlowConfiguration in formData
            processFlowConfiguration: apiData.processFlowConfiguration || {},
            // Do NOT include materialDetails - Clinical Supply Product Development doesn't show Material Details
            fgStatuses: normalizeStatusData(apiData.productFGStatuses?.fgStatusesJSON || apiData.fgStatuses || {}),
          });
          setOriginalFormData({
            productDetails: apiData.productDetails ? JSON.parse(JSON.stringify({
              ...apiData.productDetails,
              productModality: apiData.productDetails?.drugTypeName,
              manufacturingFacilityType: apiData.productDetails?.facilityTypeName,
              // Map qaCheckRequired from processFlowConfiguration (like commercial supply)
              qaCheckRequired: apiData.processFlowConfiguration?.sponsorCQAApprovalReq ?? 
                              apiData.processFlowConfiguration?.qaCheckRequired ?? 
                              apiData.productDetails?.qaCheckRequired ?? 
                              apiData.productDetails?.sponsorCQAApprovalReq ?? false,
            })) : {}, // Deep copy to preserve all fields including cmoProductDetailsList
            processFlowConfiguration: apiData.processFlowConfiguration ? JSON.parse(JSON.stringify(apiData.processFlowConfiguration)) : {},
            // Do NOT include materialDetails - Clinical Supply Product Development doesn't show Material Details
            fgStatuses: normalizeStatusData(apiData.productFGStatuses?.fgStatusesJSON || apiData.fgStatuses || {}),
          });
        }
      }
      // Show success popup - matching Commercial Supply
      setShowSuccessPopup(true);
    } catch (e) {
      console.error("Error updating product:", e);
      setError("An error occurred while updating product details.");
    } finally {
      setPageLoading(false);
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !formData.productDetails) {
    return (
      <Box p={3}>
        <Typography color="error">{error || "Product not found"}</Typography>
        <Button onClick={handleGoBack} style={{ marginTop: 16 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  // Ensure we only render the 2 sections for Clinical Supply Product Development
  const clinicalSections = sections.filter(section => 
    section.key === "productDetails" || section.key === "statusSelection"
  );

  return (
    <>
      <Paper className={`${classes.root} view-product-wrapper`}>
        <Box className={classes.stepperHeader}>
          <Typography className={classes.heading} variant="h5">
            {isEditMode ? "Update product details" : "View product details"}
          </Typography>
          <Typography className={classes.subheading}>
            {isEditMode ? "Please update the product details and status selection in the fields below" : "Please view the product details and configurations in the fields below"}
          </Typography>
          <Box className={`close-page-action`} onClick={handleGoBack}>
            <Typography>Cancel</Typography>
            <CloseIcon />
          </Box>
        </Box>
        <div className="view-product-wrapper-content">
          <div className={`stepper-container config-product-stepper-wrapper`}>
            {formData?.productDetails ? (
              clinicalSections.map(({ key, label, Component, stepKey, additionalProps = {} }, idx) => (
                <Paper elevation={0} className={classes.sectionPaper} key={key}
                  ref={
                    key === "productDetails" ? productDetailsRef :
                    key === "statusSelection" ? statusSelectionRef :
                    undefined
                  }
                >
                  <Box className={classes.reviewSection}>
                    {/* In edit mode, show edit icon button - matching Commercial Supply ViewProductEdit */}
                    {isEditMode && (
                      <Box className="section-actions" display="flex" justifyContent="space-between" alignItems="center">
                        {editSection !== key ? (
                          <IconButton
                            className={classes.iconButtonStyle}
                            onClick={() => {
                              setEditSection(key);
                              if (key === "productDetails") {
                                setIsProductDetailsEditMode(true);
                              } else if (key === "statusSelection") {
                                setIsStatusEditMode(true);
                              }
                            }}
                            disabled={!!editSection}
                          >
                            <EditIcon />
                          </IconButton>
                        ) : (
                          <Box className={classes.actionButtons}>
                            <Button
                              variant='outlined'
                              className={classes.discardButton}
                              onClick={() => {
                                handleDiscard(key);
                                if (key === "productDetails") {
                                  setIsProductDetailsEditMode(false);
                                } else if (key === "statusSelection") {
                                  setIsStatusEditMode(false);
                                }
                              }}
                              disabled={saving[key]}
                            >
                              Discard Changes
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              disableElevation
                              disableRipple
                              disableFocusRipple
                              className={classes.saveButton}
                              onClick={() => {
                                saveHandlers[key]();
                                if (key === "statusSelection") {
                                  setIsStatusEditMode(false);
                                }
                              }}
                              disabled={
                                (key === "productDetails" && (!isProductDetailsChanged || !isProductDetailsValid || saving.productDetails)) ||
                                (key === "statusSelection" && (!isStatusSelectionChanged || !isStatusSelectionValid || saving.statusSelection))
                              }
                            >
                              {saving[key] ? (
                                <>
                                  Saving
                                  <CircularProgress size={20} className={classes.buttonProgress} />
                                </>
                              ) : (
                                "Save Changes"
                              )}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                    {/* Component renders its own title - matching Commercial Supply */}
                    <Component
                      key={key === "productDetails" ? productDetailsKey : key}
                      formData={formData}
                      classes={classes}
                      readOnly={!isEditMode || editSection === null || editSection !== key}
                      updateStepData={updateStepData}
                      stepsLength={1}
                      activeStep={0}
                      handleNext={key === "productDetails" ? undefined : () => {}}
                      handleBack={key === "productDetails" ? undefined : () => {}}
                      handleSubmit={() => {}}
                      markApiCallSuccessful={() => {}}
                      selectedProductFormData={formData}
                      {...additionalProps}
                    />
                    {isEditMode && editSection === key && getValidationErrorMessage(key) && (
                      <Typography
                        color="error"
                        variant="body2"
                        style={{
                          marginTop: '8px',
                          fontSize: '0.875rem',
                        }}
                      >
                        {getValidationErrorMessage(key)}
                      </Typography>
                    )}
                  </Box>
                  {idx < clinicalSections.length - 1 && <Divider />}
                </Paper>
              ))
            ) : (
              <Typography color="error" className={classes.noProductDataText}>
                No product data available. Please select a product from the list.
              </Typography>
            )}
            {isEditMode && (
              <Box display="flex" alignItems="center" justifyContent="center" mt={5} mb={5}>
                <Button
                  className={classes.updateChangesButton}
                  variant="contained"
                  onClick={handleUpdateAllChanges}
                  disabled={!anySectionChanged || pageLoading || !!editSection}
                  endIcon={<ArrowForwardIosIcon style={{ fontSize: 18 }} />}
                >
                  Update Changes
                </Button>
              </Box>
            )}
          </div>
        </div>
      </Paper>
      {pageLoading && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255,255,255,0.6)"
          zIndex={2000}
          style={{ zIndex: 2000 }}
        >
          <CircularProgress size={60} color="primary" />
        </Box>
      )}
      {/* Success Dialog - matching Commercial Supply ViewProductEdit */}
      {isEditMode && (
        <Dialog
          open={showSuccessPopup}
          onClose={() => {
            setShowSuccessPopup(false);
            setIsRedirecting(true);
            setTimeout(() => {
              history.push(
                `/pcmp/Pharma Co/${roleName}/${organizationName}/${ecosystemId}/products`
              );
            }, 500);
          }}
          fullWidth
          className="product-updated-success-popup"
          maxWidth="sm"
        >
          <StyledDialog
            title="Product Updated"
            subtitle="The product details have been updated successfully."
            icon={false}
            handleClose={() => {
              setShowSuccessPopup(false);
              setIsRedirecting(true);
              setTimeout(() => {
                history.push(
          `/pcmp/Pharma Co/${roleName}/${organizationName}/${ecosystemId}/products`
        );
              }, 500);
            }}
            noCloseButton={true}
          />
        </Dialog>
      )}
      {isRedirecting && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255,255,255,0.8)"
          zIndex={3000}
        >
          <CircularProgress size={60} color="primary" />
        </Box>
      )}
    </>
  );
};

export default ViewProductDevelopment;
